/**
 * useInvoices Hook
 *
 * Fetches invoice data for the authenticated tenant using real GraphQL queries.
 */

import {
  useListPaymentTenantInvoicesQuery,
  useGetPaymentInvoiceQuery,
  type ListPaymentTenantInvoicesQuery,
  type GetPaymentInvoiceQuery,
  type PaymentStatus,
} from '@bloomcode/graphql';
import { useTenantContext } from './useTenantContext';

type Invoice = NonNullable<ListPaymentTenantInvoicesQuery['listPaymentTenantInvoices']['data'][number]>;
type InvoiceDetail = NonNullable<GetPaymentInvoiceQuery['getPaymentInvoice']>;

interface UseInvoicesOptions {
  page?: number;
  limit?: number;
  status?: PaymentStatus;
}

export function useInvoices(options: UseInvoicesOptions = {}) {
  const { page = 1, limit = 20, status } = options;
  const { tenantId, isValid } = useTenantContext();

  const { data, loading, error, refetch } = useListPaymentTenantInvoicesQuery({
    variables: {
      tenantId: tenantId!,
      page,
      limit,
      ...(status ? { status } : {}),
    },
    skip: !isValid,
    fetchPolicy: 'cache-and-network',
  });

  const invoices: Invoice[] = data?.listPaymentTenantInvoices?.data ?? [];
  const pagination = data?.listPaymentTenantInvoices?.pagination;

  return {
    invoices,
    pagination,
    loading,
    error: error ?? null,
    refetch,
  };
}

export function useInvoiceDetail(invoiceId: string | undefined) {
  const { data, loading, error, refetch } = useGetPaymentInvoiceQuery({
    variables: { id: invoiceId! },
    skip: !invoiceId,
    fetchPolicy: 'cache-and-network',
  });

  const invoice: InvoiceDetail | null = data?.getPaymentInvoice ?? null;

  return {
    invoice,
    loading,
    error: error ?? null,
    refetch,
  };
}

/**
 * Compute dashboard summary stats from invoice list
 */
export function useInvoiceSummary() {
  const { invoices, loading, error, refetch } = useInvoices({ limit: 100 });

  const totalOutstanding = invoices
    .filter((inv) => inv.status === 'OPEN' || inv.status === 'OVERDUE')
    .reduce((sum, inv) => sum + (inv.amount_remaining ?? 0), 0);

  const paidThisMonth = (() => {
    const now = new Date();
    return invoices
      .filter((inv) => {
        if (inv.status !== 'PAID' || !inv.paid_at) return false;
        const paidDate = new Date(inv.paid_at);
        return paidDate.getMonth() === now.getMonth() && paidDate.getFullYear() === now.getFullYear();
      })
      .reduce((sum, inv) => sum + (inv.amount_paid ?? 0), 0);
  })();

  const overdueCount = invoices.filter((inv) => inv.status === 'OVERDUE').length;

  const nextDue = invoices
    .filter((inv) => (inv.status === 'OPEN' || inv.status === 'OVERDUE') && inv.due_date)
    .sort((a, b) => new Date(a.due_date!).getTime() - new Date(b.due_date!).getTime())[0] ?? null;

  const recentInvoices = [...invoices]
    .sort((a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime())
    .slice(0, 5);

  return {
    totalOutstanding,
    paidThisMonth,
    overdueCount,
    nextDue,
    recentInvoices,
    loading,
    error,
    refetch,
  };
}
